import { useCallback, useState } from 'react';
import { callAction } from '@/api/agency-action-api';
import { ActionData, Directives } from '@/schemas/actionDataSchema';
import { decodeSubmission } from '@/schemas/validation/ValidationSchemaGenerator';
import { RouteFn } from './use-flow';
import { reportError, reportMessage } from '@/services/error-reporting-service';

interface UseActionResult {
  isLoading: boolean;
  executeAction: (name: string, args: Record<string, unknown>, routeFn: RouteFn) => void;
  submit: (name: string, submissionData: Record<string, unknown>, routeFn: RouteFn) => void;
  submitAsync: (name: string, submissionData: Record<string, unknown>, routeFn: RouteFn) => Promise<void>;
}

export function useAction(): UseActionResult {
  const [isLoading, setIsLoading] = useState(false);

  const handleError = useCallback((error: unknown) => {
    reportError({ error });
    setIsLoading(false);
  }, []);

  const runDirective = useCallback((routeFn: RouteFn, actionData: ActionData) => {
    if (actionData == null) {
      reportMessage({ message: 'Error executing action: No data returned' });
      return void refresh();
    }
    if ('message' in actionData) {
      throw new Error(actionData.message ?? undefined);
    }

    if (actionData.directive === Directives.REFRESH) {
      return void refresh();
    }

    if (actionData.directive === Directives.ROUTE) {
      return void routeFn(actionData.routing.entity_index, actionData.routing.step_id);
    }
  }, []);

  const executeAction = useCallback(
    (name: string, args: Record<string, unknown> = {}, routeFn: RouteFn): void => {
      setIsLoading(true);

      try {
        return void callAction(name, args).then((data) => {
          runDirective(routeFn, data);
          setIsLoading(false);
        });
      } catch (error) {
        handleError(error);
      } finally {
        setIsLoading(false);
      }
    },
    [handleError, runDirective]
  );

  const submitAsync = useCallback(
    async (name: string, submissionData: Record<string, unknown>, routeFn: RouteFn): Promise<void> => {
      try {
        setIsLoading(true);

        const data = await callAction(name, decodeSubmission(submissionData));

        runDirective(routeFn, data);
        setIsLoading(false);
      } catch (error) {
        handleError(error);
      }
    },
    [handleError, runDirective]
  );

  const submit = useCallback(
    (name: string, submissionData: Record<string, unknown>, routeFn: RouteFn): void => {
      try {
        setIsLoading(true);

        return void callAction(name, decodeSubmission(submissionData)).then((data) => {
          runDirective(routeFn, data);
          setIsLoading(false);
        });
      } catch (error) {
        handleError(error);
      }
    },
    [handleError, runDirective]
  );

  const refresh = () => {
    window.history.pushState({}, '', window.location.origin);
    window.dispatchEvent(new Event('popstate'));
  };

  return { isLoading, executeAction, submit, submitAsync };
}
